// om-wind-arrow-layer.ts — MapLibre CustomLayerInterface that renders 10m wind as
// an animated FREE-PARTICLE flow with fading comet trails (the wind-js / earth /
// Ventusky technique). Colour encodes sustained speed; gusts show as faster +
// brighter bursts. (Class name kept for import stability; it renders particles.)
//
// ARCHITECTURE — CPU simulation + map.project (the wind-js approach):
//   Particles are simulated on the CPU in float64 and stored as geographic
//   (lng, lat). Each frame, for every particle we:
//     • map.project([lng,lat]) → its screen position (handles mercator OR globe,
//       tilt, rotation, and any zoom, all in MapLibre's own float64 projection),
//     • sample the wind grid at (lng,lat) (JS bilinear),
//     • find the on-screen flow direction by projecting a small geographic step
//       along the wind and normalizing the screen delta,
//     • advance (lng,lat) so the particle moves a CONSTANT number of screen pixels
//       per second per m/s — independent of zoom, projection, screen size, fps.
//   The screen positions (clip space) + per-particle colour are uploaded to one VBO
//   and drawn as POINTS into a screen-sized trail FBO that fades each frame; the
//   trail is composited onto the map and cleared on camera move.
//
//   Why CPU (vs the old GPU ping-pong in float32 global mercator): storing positions
//   in global mercator [0,1] as float32 means the per-frame step falls below float
//   precision at high zoom, so motion quantizes into a strobe (worst on mobile —
//   high fps + small screen shrink the step). float64 + map.project sidesteps that
//   entirely and is correct under globe/tilt where no GPU-side workaround exists.
//   It trades raw particle count for correctness (a few thousand, like wind-js).

import type { CustomLayerInterface, CustomRenderMethod, Map as MlMap } from 'maplibre-gl'
import proj4 from 'proj4'
import { makeRotatedTransform } from './om-rotated.js'
import { buildLUT, LUT_SIZE, type Colormap } from './om-colormap.js'
import type { ViewportField } from './om-tiles.js'

// Raw fill sentinel in the decoded Int32 values (INT16_MAX); → NaN (no data).
const OM_FILL_SENTINEL_RAW = 32767
// Resampled wind grid cap (per axis): keeps the JS sampling grid bounded.
const WIND_TEX_MAX = 1024
// CPU particle budget. Fewer than the old GPU sim, but smooth on mobile (wind-js
// runs a few thousand). Each particle costs 2 map.project() calls per frame.
const PARTICLE_COUNT = 2400
// Visual tuning (speeds in the field's native SI units, m/s — model-agnostic).
const GUST_REF = 12 // gustiness (gust-speed) m/s mapped to full burst
const MIN_SPEED = 0.3 // m/s below which a particle isn't drawn (calm)
// On-screen velocity: CSS px/sec per m/s of wind, fixed (same pixel speed on every
// device — zoom-independent because we advect in screen px). Trail length scales with
// this × the fade window, so it's also the main "longer trails" lever alongside FADE.
const VIS_PX_PER_MS = 9.0
const POINT_PX = 2.0 // CSS px particle size (thinner → cleaner streaks)
const POINT_GUST_PX = 1.8 // extra size on a gust burst
const FADE = 0.97 // trail persistence per 1/60 s (fps-normalized in render; higher = longer)
const DROP_RATE = 0.0025 // base per-frame random respawn probability
// Frames before a particle respawns. Kept ≥ the visible trail window (set by FADE) so a
// streak draws fully before the particle jumps; raise FADE+MAX_AGE together for longer tails.
const MAX_AGE = 400
const GUST_SPEED_BOOST = 0.6 // fractional speed surge at full gust on a pulse
// Geographic step (degrees) used to probe the on-screen flow direction via two
// projections. Small enough to be local, large enough to project to a stable vector.
const EPS_DEG = 0.05

// Particle colour ramp = SUSTAINED speed. Vivid + fully opaque.
const STREAM_RAMP: Colormap = {
  stops: [
    { t: 0.0, c: [70, 140, 225] }, // light wind — blue
    { t: 0.2, c: [40, 195, 205] }, // teal
    { t: 0.4, c: [80, 205, 115] }, // green
    { t: 0.6, c: [240, 220, 75] }, // yellow
    { t: 0.8, c: [240, 145, 50] }, // orange
    { t: 1.0, c: [225, 45, 55] }, // strong — red
  ],
  domainMin: 0,
  domainMax: 28, // m/s (~63 mph) → red
}

export interface OmWindArrowLayerOptions {
  id: string
  u: ViewportField | null
  v: ViewportField | null
  gust: ViewportField | null
  opacity?: number
}

interface WindData {
  data: Float32Array // nx*ny*4, RGBA = [u, v, gust(or NaN), 1], row 0 = south
  nx: number
  ny: number
  bbox: [number, number, number, number] // [latMin, lonMin, latMax, lonMax]
}

// ---------------------------------------------------------------------------
// Resample u/v/gust (sharing one grid) → a regular lat/lon grid (WindData).
// ---------------------------------------------------------------------------

function toPhysical(f: ViewportField): Float32Array {
  const out = new Float32Array(f.values.length)
  for (let i = 0; i < f.values.length; i++) {
    out[i] = f.values[i] === OM_FILL_SENTINEL_RAW ? NaN : f.values[i] / f.scale
  }
  return out
}

function sampleBilinear(
  arr: Float32Array,
  nLon: number,
  nLat: number,
  col: number,
  row: number
): number {
  if (col < 0 || col > nLon - 1 || row < 0 || row > nLat - 1) return NaN
  const c0 = Math.floor(col)
  const r0 = Math.floor(row)
  const c1 = Math.min(c0 + 1, nLon - 1)
  const r1 = Math.min(r0 + 1, nLat - 1)
  const fc = col - c0
  const fr = row - r0
  const v00 = arr[r0 * nLon + c0]
  const v10 = arr[r0 * nLon + c1]
  const v01 = arr[r1 * nLon + c0]
  const v11 = arr[r1 * nLon + c1]
  if (Number.isNaN(v00) || Number.isNaN(v10) || Number.isNaN(v01) || Number.isNaN(v11)) return NaN
  const a = v00 + (v10 - v00) * fc
  const b = v01 + (v11 - v01) * fc
  return a + (b - a) * fr
}

function makeColRow(field: ViewportField): (lon: number, lat: number) => [number, number] {
  const { nLon, nLat, bbox, projection } = field
  const [latMin, lonMin, latMax, lonMax] = bbox
  if (!projection) {
    return (lon, lat) => [
      ((lon - lonMin) / (lonMax - lonMin)) * (nLon - 1),
      ((lat - latMin) / (latMax - latMin)) * (nLat - 1),
    ]
  }
  const { x0, y0, dx, dy, rotatedPole } = projection
  if (rotatedPole) {
    const rot = makeRotatedTransform(rotatedPole)
    return (lon, lat) => {
      const [rlon, rlat] = rot.toRot(lon, lat)
      return [(rlon - x0) / dx, (rlat - y0) / dy]
    }
  }
  const toProj = proj4(projection.proj4def, 'WGS84')
  return (lon, lat) => {
    const [x, y] = toProj.inverse([lon, lat])
    return [(x - x0) / dx, (y - y0) / dy]
  }
}

// Output lat/lon box to resample onto. For a REGULAR grid that's just the field
// bbox. For a PROJECTED/rotated grid the field's bbox under-claims the true reach,
// so scan the band's projected perimeter to the TRUE lat/lon envelope.
function fieldOutputBbox(field: ViewportField): [number, number, number, number] {
  const { bbox, projection, nLon, nLat } = field
  if (!projection) return bbox
  const { x0, y0, dx, dy, rotatedPole } = projection
  const toGeo: (x: number, y: number) => [number, number] = rotatedPole
    ? (() => {
        const rot = makeRotatedTransform(rotatedPole)
        return (x, y) => rot.toGeo(x, y)
      })()
    : (() => {
        const t = proj4(projection.proj4def, 'WGS84')
        return (x, y) => t.forward([x, y])
      })()
  let latMin = Infinity
  let lonMin = Infinity
  let latMax = -Infinity
  let lonMax = -Infinity
  const acc = (lon: number, lat: number): void => {
    if (lat < latMin) latMin = lat
    if (lat > latMax) latMax = lat
    if (lon < lonMin) lonMin = lon
    if (lon > lonMax) lonMax = lon
  }
  const xEnd = x0 + (nLon - 1) * dx
  const yEnd = y0 + (nLat - 1) * dy
  const N = 48
  for (let i = 0; i <= N; i++) {
    const x = x0 + (i / N) * (nLon - 1) * dx
    acc(...toGeo(x, y0)) // bottom row
    acc(...toGeo(x, yEnd)) // top row
    const y = y0 + (i / N) * (nLat - 1) * dy
    acc(...toGeo(x0, y)) // left column
    acc(...toGeo(xEnd, y)) // right column
  }
  return [latMin, lonMin, latMax, lonMax]
}

function buildWindData(
  u: ViewportField | null,
  v: ViewportField | null,
  gust: ViewportField | null
): WindData | null {
  if (!u || u.values.length !== v?.values.length) return null
  const { nLon, nLat } = u
  const outBbox = fieldOutputBbox(u)
  const [latMin, lonMin, latMax, lonMax] = outBbox
  const uPhys = toPhysical(u)
  const vPhys = toPhysical(v)
  const gustPhys = gust?.values.length === u.values.length ? toPhysical(gust) : null

  const nx = Math.max(2, Math.min(WIND_TEX_MAX, nLon))
  const ny = Math.max(2, Math.min(WIND_TEX_MAX, nLat))
  const colRow = makeColRow(u)
  const data = new Float32Array(nx * ny * 4)
  for (let ry = 0; ry < ny; ry++) {
    const lat = latMin + (ry / (ny - 1)) * (latMax - latMin)
    for (let rx = 0; rx < nx; rx++) {
      const lon = lonMin + (rx / (nx - 1)) * (lonMax - lonMin)
      const [col, row] = colRow(lon, lat)
      const o = (ry * nx + rx) * 4
      data[o] = sampleBilinear(uPhys, nLon, nLat, col, row)
      data[o + 1] = sampleBilinear(vPhys, nLon, nLat, col, row)
      data[o + 2] = gustPhys ? sampleBilinear(gustPhys, nLon, nLat, col, row) : NaN
      data[o + 3] = 1
    }
  }
  return { data, nx, ny, bbox: outBbox }
}

interface WindSample {
  u: number
  v: number
  gust: number
}

// JS bilinear sample of the regular lat/lon wind grid. NaN if off-grid / no-data.
function sampleWind(wind: WindData, lon: number, lat: number): WindSample {
  const [latMin, lonMin, latMax, lonMax] = wind.bbox
  const { nx, ny, data } = wind
  const fx = ((lon - lonMin) / (lonMax - lonMin)) * (nx - 1)
  const fy = ((lat - latMin) / (latMax - latMin)) * (ny - 1) // row 0 = south = latMin
  if (fx < 0 || fx > nx - 1 || fy < 0 || fy > ny - 1) return { u: NaN, v: NaN, gust: NaN }
  const x0 = Math.floor(fx)
  const y0 = Math.floor(fy)
  const x1 = Math.min(x0 + 1, nx - 1)
  const y1 = Math.min(y0 + 1, ny - 1)
  const tx = fx - x0
  const ty = fy - y0
  const at = (yy: number, xx: number, c: number): number => data[(yy * nx + xx) * 4 + c]
  const lerp2 = (c: number): number => {
    const a = at(y0, x0, c)
    const b = at(y0, x1, c)
    const cc = at(y1, x0, c)
    const d = at(y1, x1, c)
    if (Number.isNaN(a) || Number.isNaN(b) || Number.isNaN(cc) || Number.isNaN(d)) return NaN
    const top = a + (b - a) * tx
    const bot = cc + (d - cc) * tx
    return top + (bot - top) * ty
  }
  return { u: lerp2(0), v: lerp2(1), gust: lerp2(2) }
}

// ---------------------------------------------------------------------------
// Shaders. Particle draw reads pre-projected clip positions from a VBO (no
// projectTile, no state texture). Screen pass fades/blits the trail FBO.
// ---------------------------------------------------------------------------

const DRAW_VERT = `#version 300 es
in vec2 a_pos;        // clip-space [-1,1] (CPU-projected)
in float a_colorT;    // 0..1 speed
in float a_bright;    // gust brighten / size boost
uniform float u_pixelRatio;
out float v_colorT;
out float v_bright;
void main(){
  gl_Position = vec4(a_pos, 0.0, 1.0);
  gl_PointSize = (${POINT_PX.toFixed(2)} + a_bright*${POINT_GUST_PX.toFixed(2)}) * u_pixelRatio;
  v_colorT = a_colorT;
  v_bright = a_bright;
}`

const DRAW_FRAG = `#version 300 es
precision highp float;
in float v_colorT;
in float v_bright;
out vec4 o;
uniform sampler2D u_lut;
uniform float u_lutSize;
uniform float u_opacity;
void main(){
  vec2 d = gl_PointCoord - vec2(0.5);
  if (dot(d,d) > 0.25) discard;                 // round dot
  float idx = floor(v_colorT*(u_lutSize-1.0) + 0.5);
  vec3 c = texture(u_lut, vec2((idx+0.5)/u_lutSize, 0.5)).rgb;
  c = mix(c, vec3(1.0), 0.5*v_bright);          // gust → brighten
  o = vec4(c, u_opacity);                        // blend disabled in FBO (overwrite)
}`

// Fullscreen quad for the trail-fade / composite blits.
const QUAD_VERT = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main(){ v_uv = a_pos; gl_Position = vec4(1.0 - 2.0*a_pos, 0.0, 1.0); }`

// SCREEN composite: blit a texture with an opacity multiply. Sample at (1 - v_uv)
// to cancel the QUAD_VERT flip → the trail fade is an identity blit. floor() lets a
// faded trail reach 0.
const SCREEN_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 o;
uniform sampler2D u_tex;
uniform float u_opacity;
void main(){ o = floor(255.0 * texture(u_tex, 1.0 - v_uv) * u_opacity) / 255.0; }`

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)
  if (!sh) throw new Error('om-wind-arrow-layer: createShader failed')
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh)
    gl.deleteShader(sh)
    throw new Error('om-wind-arrow-layer: shader compile failed: ' + log)
  }
  return sh
}

function linkProgram(gl: WebGL2RenderingContext, vertSrc: string, fragSrc: string): WebGLProgram {
  const program = gl.createProgram()
  if (!program) throw new Error('om-wind-arrow-layer: createProgram failed')
  const vs = compileShader(gl, gl.VERTEX_SHADER, vertSrc)
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc)
  gl.attachShader(program, vs)
  gl.attachShader(program, fs)
  gl.linkProgram(program)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program)
    gl.deleteProgram(program)
    throw new Error('om-wind-arrow-layer: program link failed: ' + log)
  }
  return program
}

function makeTexture(gl: WebGL2RenderingContext): WebGLTexture {
  const t = gl.createTexture()
  if (!t) throw new Error('om-wind-arrow-layer: createTexture failed')
  return t
}

export class OmWindArrowLayer implements CustomLayerInterface {
  readonly id: string
  readonly type = 'custom' as const
  readonly renderingMode = '2d' as const

  private map: MlMap | null = null
  private gl: WebGL2RenderingContext | null = null
  private disabled = false

  private opacity: number
  private colormap: Colormap = STREAM_RAMP
  private wind: WindData | null

  private drawProgram: WebGLProgram | null = null
  private screenProgram: WebGLProgram | null = null
  private quadBuffer: WebGLBuffer | null = null
  private particleBuffer: WebGLBuffer | null = null
  private fbo: WebGLFramebuffer | null = null
  private lutTex: WebGLTexture | null = null
  private screenTex: [WebGLTexture, WebGLTexture] | null = null
  private texW = 0
  private texH = 0

  // CPU particle state (float64 geographic) + the interleaved GPU draw buffer.
  private lng = new Float64Array(PARTICLE_COUNT)
  private lat = new Float64Array(PARTICLE_COUNT)
  private age = new Float32Array(PARTICLE_COUNT)
  private particleData = new Float32Array(PARTICLE_COUNT * 4) // clipX, clipY, colorT, bright
  private seeded = false

  private lastTime = 0
  private needsClear = true
  private onMove = (): void => {
    this.needsClear = true
  }

  constructor(opts: OmWindArrowLayerOptions) {
    this.id = opts.id
    this.opacity = opts.opacity ?? 1
    this.wind = buildWindData(opts.u, opts.v, opts.gust)
  }

  // ---- public mutators ----

  setField(u: ViewportField | null, v: ViewportField | null, gust: ViewportField | null): void {
    this.wind = buildWindData(u, v, gust)
    this.seeded = false // reseed into the new field footprint
    this.needsClear = true
    this.map?.triggerRepaint()
  }

  setOpacity(opacity: number): void {
    this.opacity = Math.max(0, Math.min(1, opacity))
    this.map?.triggerRepaint()
  }

  // ---- CustomLayerInterface ----

  onAdd(map: MlMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (!(gl instanceof WebGL2RenderingContext)) {
      throw new Error('om-wind-arrow-layer requires a WebGL2 context')
    }
    this.map = map
    this.gl = gl

    this.drawProgram = linkProgram(gl, DRAW_VERT, DRAW_FRAG)
    this.screenProgram = linkProgram(gl, QUAD_VERT, SCREEN_FRAG)

    this.quadBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW)

    this.particleBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, this.particleData.byteLength, gl.DYNAMIC_DRAW)

    this.fbo = gl.createFramebuffer()
    this.lutTex = makeTexture(gl)
    this.uploadLut(gl)

    map.on('move', this.onMove)
  }

  onRemove(_map: MlMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    this.map?.off('move', this.onMove)
    if (!(gl instanceof WebGL2RenderingContext)) return
    if (this.drawProgram) gl.deleteProgram(this.drawProgram)
    if (this.screenProgram) gl.deleteProgram(this.screenProgram)
    if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer)
    if (this.particleBuffer) gl.deleteBuffer(this.particleBuffer)
    if (this.fbo) gl.deleteFramebuffer(this.fbo)
    if (this.lutTex) gl.deleteTexture(this.lutTex)
    if (this.screenTex) this.screenTex.forEach(t => gl.deleteTexture(t))
    this.gl = null
    this.map = null
  }

  render: CustomRenderMethod = (glLike, _options) => {
    const gl = this.gl
    const map = this.map
    if (!gl || glLike !== gl || this.disabled || !this.wind || !map) return

    const mapFb = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null
    const vp = gl.getParameter(gl.VIEWPORT) as Int32Array

    this.ensureScreenTextures(gl)

    const now = performance.now() / 1000
    const dt = this.lastTime ? Math.min(0.05, now - this.lastTime) : 0.016
    this.lastTime = now

    if (this.needsClear) {
      this.clearScreen(gl)
      this.needsClear = false
    }
    if (!this.seeded) this.seedParticles()

    // CPU step: advect + project all particles, fill the draw buffer.
    this.updateCpu(now, dt)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffer)
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.particleData)

    gl.disable(gl.DEPTH_TEST)
    gl.disable(gl.STENCIL_TEST)
    gl.disable(gl.BLEND)

    // FADE is calibrated per 1/60 s; raise it to a per-frame value for the actual frame
    // time so trail length is the same regardless of frame rate (the phone runs ~2× the
    // desktop's fps, which would otherwise fade trails twice as fast / make them shorter).
    const frameFade = Math.pow(FADE, dt * 60)
    this.drawToScreen(gl, frameFade)
    this.composite(gl, mapFb, vp)
    this.swap()

    map.triggerRepaint()
  }

  // ---- internals ----

  private uploadLut(gl: WebGL2RenderingContext): void {
    const lut = buildLUT(this.colormap)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.lutTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, LUT_SIZE, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, lut)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  }

  // Region to (re)seed particles into: the current viewport ∩ the field footprint.
  private seedRegion(): { w: number; s: number; spanLon: number; spanLat: number } | null {
    const map = this.map
    if (!map || !this.wind) return null
    const b = map.getBounds()
    const [fLatMin, fLonMin, fLatMax, fLonMax] = this.wind.bbox
    const vw = b.getWest()
    let ve = b.getEast()
    if (ve < vw) ve += 360 // antimeridian span
    const w = Math.max(vw, fLonMin)
    const e = Math.min(ve, fLonMax)
    const s = Math.max(b.getSouth(), fLatMin)
    const n = Math.min(b.getNorth(), fLatMax)
    if (e <= w || n <= s) return null // viewport doesn't overlap the data
    return { w, s, spanLon: e - w, spanLat: n - s }
  }

  private seedParticles(): void {
    const r = this.seedRegion()
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      if (r) {
        this.lng[i] = r.w + Math.random() * r.spanLon
        this.lat[i] = r.s + Math.random() * r.spanLat
      }
      this.age[i] = Math.random() * MAX_AGE
    }
    this.seeded = true
  }

  private updateCpu(now: number, dt: number): void {
    const map = this.map
    const wind = this.wind
    if (!map || !wind) return
    const canvas = map.getCanvas()
    const cssW = canvas.clientWidth || 1280
    const cssH = canvas.clientHeight || 1280
    const halfW = cssW * 0.5
    const halfH = cssH * 0.5
    const r = this.seedRegion()
    const buf = this.particleData
    const RAD = Math.PI / 180

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      let lo = this.lng[i]
      let la = this.lat[i]
      let p = map.project([lo, la])
      let s = sampleWind(wind, lo, la)
      let spd = Math.hypot(s.u, s.v)

      const offscreen = p.x < 0 || p.x > cssW || p.y < 0 || p.y > cssH
      const invalid = Number.isNaN(spd) || spd < MIN_SPEED
      if (this.age[i] > MAX_AGE || invalid || offscreen || Math.random() < DROP_RATE) {
        if (!r) {
          buf[i * 4] = 2 // park off-screen → not drawn
          buf[i * 4 + 3] = 0
          continue
        }
        lo = r.w + Math.random() * r.spanLon
        la = r.s + Math.random() * r.spanLat
        this.lng[i] = lo
        this.lat[i] = la
        this.age[i] = Math.random() * MAX_AGE
        p = map.project([lo, la])
        s = sampleWind(wind, lo, la)
        spd = Math.hypot(s.u, s.v)
        if (Number.isNaN(spd) || spd < MIN_SPEED) {
          buf[i * 4] = 2
          buf[i * 4 + 3] = 0
          continue
        }
      }

      // Gust burst (cheap per-particle pulse).
      const gustiness = Number.isNaN(s.gust) ? 0 : Math.max(0, s.gust - spd)
      const pulse = 0.5 + 0.5 * Math.sin(now * 0.9 + i * 0.61)
      const burst = Math.min(1, gustiness / GUST_REF) * pulse
      const gustMul = 1 + GUST_SPEED_BOOST * burst

      // On-screen flow direction: project a small geographic step ALONG the wind
      // (true east/north, lat-corrected) and take the screen delta. Handles globe,
      // tilt, rotation, and any zoom — the projection does the work.
      const cosLat = Math.max(0.01, Math.cos(la * RAD))
      const inv = 1 / spd
      const dLon = ((s.u * inv) / cosLat) * EPS_DEG
      const dLat = s.v * inv * EPS_DEG
      const p1 = map.project([lo + dLon, la + dLat])
      const sdx = p1.x - p.x
      const sdy = p1.y - p.y
      const sLen = Math.hypot(sdx, sdy)
      if (sLen > 1e-4) {
        // Move a CONSTANT number of screen px (per m/s) regardless of zoom: convert
        // that px target back into a geographic move along the probe vector.
        const pxMove = VIS_PX_PER_MS * spd * gustMul * dt
        const k = pxMove / sLen
        this.lng[i] = lo + dLon * k
        this.lat[i] = la + dLat * k
      }
      this.age[i]++

      // Write the CURRENT projected position (clip) + colour for this frame.
      buf[i * 4] = p.x / halfW - 1
      buf[i * 4 + 1] = 1 - p.y / halfH
      buf[i * 4 + 2] = Math.max(0, Math.min(1, spd / STREAM_RAMP.domainMax))
      buf[i * 4 + 3] = burst
    }
  }

  private ensureScreenTextures(gl: WebGL2RenderingContext): void {
    const w = gl.drawingBufferWidth
    const h = gl.drawingBufferHeight
    if (this.screenTex && this.texW === w && this.texH === h) return
    if (this.screenTex) this.screenTex.forEach(t => gl.deleteTexture(t))
    const empty = new Uint8Array(w * h * 4)
    const mk = (): WebGLTexture => {
      const t = makeTexture(gl)
      gl.bindTexture(gl.TEXTURE_2D, t)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, empty)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      return t
    }
    this.screenTex = [mk(), mk()]
    this.texW = w
    this.texH = h
    this.needsClear = true
  }

  private bindFbColor(gl: WebGL2RenderingContext, tex: WebGLTexture): void {
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0)
  }

  private clearScreen(gl: WebGL2RenderingContext): void {
    if (!this.screenTex) return
    for (const t of this.screenTex) {
      this.bindFbColor(gl, t)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
    }
  }

  private drawToScreen(gl: WebGL2RenderingContext, fade: number): void {
    if (!this.screenTex) return
    this.bindFbColor(gl, this.screenTex[1])
    gl.viewport(0, 0, this.texW, this.texH)
    this.drawTexture(gl, this.screenTex[0], fade) // faded previous trails
    this.drawPoints(gl) // new particles on top
  }

  private drawTexture(gl: WebGL2RenderingContext, tex: WebGLTexture, opacity: number): void {
    if (!this.screenProgram) return
    gl.useProgram(this.screenProgram)
    this.bindQuad(gl, this.screenProgram)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.uniform1i(gl.getUniformLocation(this.screenProgram, 'u_tex'), 0)
    gl.uniform1f(gl.getUniformLocation(this.screenProgram, 'u_opacity'), opacity)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
  }

  private drawPoints(gl: WebGL2RenderingContext): void {
    const prog = this.drawProgram
    if (!prog || !this.particleBuffer) return
    gl.useProgram(prog)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffer)
    const stride = 16
    const aPos = gl.getAttribLocation(prog, 'a_pos')
    const aColorT = gl.getAttribLocation(prog, 'a_colorT')
    const aBright = gl.getAttribLocation(prog, 'a_bright')
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, stride, 0)
    gl.enableVertexAttribArray(aColorT)
    gl.vertexAttribPointer(aColorT, 1, gl.FLOAT, false, stride, 8)
    gl.enableVertexAttribArray(aBright)
    gl.vertexAttribPointer(aBright, 1, gl.FLOAT, false, stride, 12)

    const cssW = this.map?.getCanvas().clientWidth || gl.drawingBufferWidth
    const dpr = cssW > 0 ? gl.drawingBufferWidth / cssW : 1
    gl.uniform1f(gl.getUniformLocation(prog, 'u_pixelRatio'), dpr)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.lutTex)
    gl.uniform1i(gl.getUniformLocation(prog, 'u_lut'), 0)
    gl.uniform1f(gl.getUniformLocation(prog, 'u_lutSize'), LUT_SIZE)
    gl.uniform1f(gl.getUniformLocation(prog, 'u_opacity'), this.opacity)
    gl.drawArrays(gl.POINTS, 0, PARTICLE_COUNT)
  }

  private composite(
    gl: WebGL2RenderingContext,
    mapFb: WebGLFramebuffer | null,
    vp: Int32Array
  ): void {
    if (!this.screenTex) return
    gl.bindFramebuffer(gl.FRAMEBUFFER, mapFb)
    gl.viewport(vp[0], vp[1], vp[2], vp[3])
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    this.drawTexture(gl, this.screenTex[1], 1.0)
    gl.disable(gl.BLEND)
  }

  private swap(): void {
    if (this.screenTex) this.screenTex = [this.screenTex[1], this.screenTex[0]]
  }

  private bindQuad(gl: WebGL2RenderingContext, program: WebGLProgram): void {
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    const a = gl.getAttribLocation(program, 'a_pos')
    gl.enableVertexAttribArray(a)
    gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0)
  }
}
